import React from 'react';
import { 
  ShieldCheck, Lock, ArrowRight, CheckCircle2, 
  AlertCircle, Coins, CreditCard, Wallet, 
  X, Info, Clock, Smartphone, Globe, RefreshCw
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';
import { convertCurrency, formatCurrency as formatLocalCurrency, LATEST_RATES, calculateSafePayFee } from '../utils/exchange-utils';

interface SafePayEscrowProps {
  isOpen: boolean;
  onClose: () => void;
  item: {
    title: string;
    price: number;
    seller: string;
    currency: string;
    country?: string; // e.g., 'Nigeria', 'USA', 'UK', 'Ghana'
  };
  userBalance: number; // In user's base currency (e.g., NGN)
  userCurrency: string; // e.g., 'NGN'
  onConfirm: (amount: number) => void;
}

export function SafePayEscrow({ isOpen, onClose, item, userBalance, userCurrency, onConfirm }: SafePayEscrowProps) {
  const [step, setStep] = React.useState(1);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [exchangeRate, setExchangeRate] = React.useState<number>(1);

  // Map symbols to currency codes for utility
  const symbolToCode: Record<string, string> = {
    '₦': 'NGN',
    '$': 'USD',
    '£': 'GBP',
    'GH₵': 'GHS',
    '€': 'EUR'
  };

  const itemCurrencyCode = symbolToCode[item.currency] || 'NGN';
  const isCrossBorder = userCurrency !== itemCurrencyCode;

  // Calculate equivalent cost in user's currency
  const amountInUserCurrency = convertCurrency(item.price, itemCurrencyCode, userCurrency);
  const transactionFee = calculateSafePayFee(amountInUserCurrency, userCurrency, itemCurrencyCode);
  const totalCharge = amountInUserCurrency + transactionFee;

  React.useEffect(() => {
    if (isCrossBorder) {
      // 1 unit of user currency = ? unit of item currency
      const rate = convertCurrency(1, userCurrency, itemCurrencyCode);
      setExchangeRate(rate);
    }
  }, [userCurrency, itemCurrencyCode, isCrossBorder]);

  const handleProcess = () => {
    setIsProcessing(true);
    // Simulate biometric/PIN verification
    setTimeout(() => {
      const pin = prompt('Enter your 4-digit Sentlo PIN to authorize escrow:');
      if (pin !== '1234') {
        toast.error('Incorrect PIN', { description: 'Security verification failed.' });
        setIsProcessing(false);
        return;
      }
      
      setTimeout(() => {
        setIsProcessing(false);
        setStep(3);
        onConfirm(totalCharge);
      }, 1000);
    }, 500);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm">
      <motion.div 
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        className="w-full max-w-md bg-white rounded-t-[32px] sm:rounded-[32px] overflow-hidden shadow-2xl"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-lg font-black text-gray-900 uppercase italic">Sentlo SafePay</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div 
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="bg-zinc-50 p-4 rounded-2xl border border-zinc-100">
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Item Details</p>
                    {isCrossBorder && (
                      <div className="flex items-center gap-1.5 px-2 py-0.5 bg-blue-50 border border-blue-100 rounded-full">
                        <Globe className="w-2.5 h-2.5 text-blue-600" />
                        <span className="text-[9px] font-black text-blue-700 uppercase">Cross-Border</span>
                      </div>
                    )}
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="font-bold text-gray-900">{item.title}</h3>
                      <p className="text-xs text-gray-500">Seller: {item.seller}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-black text-green-700">{item.currency}{item.price.toLocaleString()}</p>
                      {isCrossBorder && (
                        <p className="text-[10px] font-bold text-zinc-400 italic">
                          ≈ {formatLocalCurrency(amountInUserCurrency, userCurrency)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {isCrossBorder && (
                    <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50/50 border border-blue-100">
                      <RefreshCw className="w-5 h-5 text-blue-600 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-blue-900">Live Exchange Rate</p>
                        <p className="text-[10px] text-blue-700 leading-relaxed">
                          1 {userCurrency} = {exchangeRate.toFixed(4)} {itemCurrencyCode}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-blue-50/50 border border-blue-100">
                    <Lock className="w-5 h-5 text-blue-600 shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-blue-900">Escrow Protection</p>
                      <p className="text-[10px] text-blue-700 leading-relaxed">Your money is held securely by Sentlo. We only release it to {item.seller} after you confirm receipt of the item.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 p-3 rounded-xl bg-amber-50/50 border border-amber-100">
                    <Info className="w-5 h-5 text-amber-600 shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-amber-900">Neighborhood Verification</p>
                      <p className="text-[10px] text-amber-700 leading-relaxed">Meet in a public place. Both of you must scan a QR code to complete the swap.</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <button 
                    onClick={() => setStep(2)}
                    className="w-full py-4 bg-green-600 text-white font-black rounded-2xl shadow-lg shadow-green-600/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 uppercase italic text-sm"
                  >
                    Continue to Payment
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div 
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-4">Choose Payment Method</h3>
                  <div className="grid gap-3">
                    <button className="flex items-center gap-4 p-4 rounded-2xl border-2 border-green-600 bg-green-50/50 text-left transition-all">
                      <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center shrink-0">
                        <Wallet className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-gray-900">Sentlo Wallet</p>
                        <p className="text-[10px] text-green-700">Balance: {formatLocalCurrency(userBalance, userCurrency)}</p>
                      </div>
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </button>
                    
                    <button className="flex items-center gap-4 p-4 rounded-2xl border border-gray-100 hover:border-zinc-200 text-left transition-all group">
                      <div className="w-10 h-10 rounded-xl bg-zinc-100 flex items-center justify-center shrink-0 group-hover:bg-zinc-200">
                        <CreditCard className="w-5 h-5 text-zinc-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-gray-900">Bank Transfer</p>
                        <p className="text-[10px] text-gray-500">Instant verification</p>
                      </div>
                    </button>
                  </div>
                </div>

                <div className="space-y-3 p-4 bg-zinc-50 rounded-2xl border border-zinc-100">
                  <div className="flex justify-between items-center text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                    <span>Base Amount ({itemCurrencyCode})</span>
                    <span className="text-gray-900">{item.currency}{item.price.toLocaleString()}</span>
                  </div>
                  {isCrossBorder && (
                    <div className="flex justify-between items-center text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                      <span>Equivalent in {userCurrency}</span>
                      <span className="text-gray-900">{formatLocalCurrency(amountInUserCurrency, userCurrency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                    <span>Service Fee ({isCrossBorder ? 'Cross-Border' : 'Standard'})</span>
                    <span className="text-blue-600">+{formatLocalCurrency(transactionFee, userCurrency)}</span>
                  </div>
                  <div className="pt-2 border-t border-zinc-200 flex justify-between items-center font-black uppercase italic">
                    <span className="text-xs text-gray-900">Total to Lock</span>
                    <span className="text-lg text-green-700">{formatLocalCurrency(totalCharge, userCurrency)}</span>
                  </div>
                </div>

                {userBalance < totalCharge && (
                  <div className="p-3 rounded-xl bg-red-50 border border-red-100 flex gap-2">
                    <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                    <p className="text-[10px] text-red-700">Insufficient balance. Please top up your wallet or use Bank Transfer.</p>
                  </div>
                )}

                <div className="pt-2">
                  <button 
                    disabled={isProcessing || userBalance < totalCharge}
                    onClick={handleProcess}
                    className="w-full py-4 bg-zinc-900 text-white font-black rounded-2xl shadow-lg shadow-zinc-900/20 active:scale-[0.98] transition-all flex items-center justify-center gap-2 uppercase italic text-sm disabled:opacity-50 disabled:active:scale-100"
                  >
                    {isProcessing ? (
                      <span className="flex items-center gap-2">
                        <Clock className="w-4 h-4 animate-spin" />
                        Securing Funds...
                      </span>
                    ) : (
                      `Authorize Escrow`
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div 
                key="step3"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="py-10 text-center space-y-6"
              >
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-xl">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-gray-900 italic uppercase">Funds Secured!</h3>
                  <p className="text-sm text-zinc-500 px-6 mt-2 leading-relaxed">
                    {formatLocalCurrency(totalCharge, userCurrency)} has been locked in SafePay escrow. You can now meet <strong className="text-gray-900">{item.seller}</strong> to collect your item.
                  </p>
                </div>
                
                <div className="bg-zinc-50 p-6 rounded-3xl border border-zinc-100 space-y-4">
                  <p className="text-[10px] font-black text-zinc-400 uppercase tracking-widest">Your Next Step</p>
                  <div className="flex flex-col items-center gap-4">
                    <div className="p-4 bg-white rounded-2xl shadow-sm border border-zinc-100">
                       <Smartphone className="w-12 h-12 text-zinc-900" />
                    </div>
                    <p className="text-xs font-bold text-zinc-700">Open QR Scanner when you meet</p>
                  </div>
                </div>

                <button 
                  onClick={onClose}
                  className="w-full py-4 bg-zinc-900 text-white font-black rounded-2xl uppercase italic text-sm"
                >
                  Done
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
}